import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  forwardRef,
  OnDestroy,
  inject
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { debounce } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { GlobalConstants } from 'src/app/base/global.constants';
import { MessageService } from 'primeng/api';
import { UtilService, validateExternalUrl } from 'src/app/services/util.service';

@Component({
  selector: 'vb-input-text',
  templateUrl: './vb-input-text.component.html',
  styleUrls: ['./vb-input-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VbInputTextComponent),
      multi: true
    }
  ],
  standalone: false  //NOSONAR
})
export class VbInputTextComponent implements OnInit, OnChanges, OnDestroy {
  private confirmationDialogService = inject(ConfirmationDialogService);
  private renderer = inject(Renderer2);
  private elementRef = inject(ElementRef);
  private translate = inject(TranslateService);
  private utilService = inject(UtilService);
  private readonly messageService = inject(MessageService);

  @Input() element: any;
  @Input() options: any;
  @Input() disableField = false;
  @Input() formData: any = null;
  @Input() currentSelectedTreeNode: any;
  @Output() public valChanges = new EventEmitter();
  @Input() currentPage: any;
  previousAction = '';
  txtVal: any;
  oldvalue: any;
  touched = false;
  plchecked: any;
  private modelValue: any;
  private onChange: (m: any) => void = () => {};
  private onTouched: (m: any) => void = () => {};
  formFieldDisable = false;
  maxChars;
  inputElementType = 'default';

  ngOnInit(): void {
    this.touched = true;
    this.previousAction = this.formData?.action;
    this.initiateBtnProps();
    if (this.element.name === 'createButtonLabel') {
      this.inputElementType = 'AlphaNumericWithSpace';
      this.maxChars = 30;
    } else if (this.element.name === 'f_label') {
      this.inputElementType = 'FieldLabel';
    }
    const disableForSysGenMenus =
      (this.currentSelectedTreeNode?.data?.sysGen || this.currentSelectedTreeNode?.data?.id === 'import-menu') &&
      this.element?.title === GlobalConstants.url;
    if (disableForSysGenMenus) {
      this.disableField = true;
    }
  }

  get model() {
    return this.modelValue;
  }

  writeValue(value: any): void {
    this.modelValue = value;
    this.txtVal = value;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  set() {
    if (this.currentPage?.label?.toLowerCase() === 'api_integration') {
      this.txtVal = this.txtVal?.trim();
    }
    if (this.element.name === 'childPageLimit' || this.element.name === 'pageLimit') {
      this.validatePageLimits();
      if (this.txtVal && this.txtVal > 100 && this.txtVal <= 1000) {
        this.showWarningPagelimit(this.txtVal);
      }
    }
    // Validate external URL and show toast if invalid
    if (this.isExternalUrlField()) {
      const validationResult = validateExternalUrl(this.txtVal);
      if (!validationResult.isValid) {
        this.showErrorToast(this.translate.instant(validationResult.errorKey!) || validationResult.errorMessage!);
        return; // Don't propagate changes if validation fails
      }
    }
    this.oldvalue = this.modelValue;
    this.modelValue = this.txtVal;
    this.onChange?.(this.modelValue);
  }

  /**
   * Checks if the current field is an external URL field
   */
  private isExternalUrlField(): boolean {
    return this.element?.uiType === 'url' && this.formData?.onClick === 'open_external_url';
  }

  /**
   * Shows an error toast message
   */
  private showErrorToast(message: string): void {
    this.messageService.clear();
    this.messageService.add({
      life: 10000,
      severity: 'error',
      summary: message
    });
  }

  setDisabledState(isDisabled: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
    this.formFieldDisable = isDisabled;
  }

  debounceValChanges = debounce(this.set.bind(this), 1000);

  valueChange(event) {
    this.debounceValChanges();
  }

  validatePageLimits() {
    const val = Number(this.txtVal);
    if (!Number.isInteger(val) || val <= 0 || val > 1000 || isNaN(val)) {
      this.txtVal = this.element.defaultValue;
    } else {
      this.txtVal = val;
    }
  }

  ngOnChanges(): void {
    if (this.formData?.f_sysF || (this.formData?.purpose && this.formData?.purpose === 'REFERENCE')) {
      this.disableField = true;
    }
    if (this.previousAction !== this.formData?.action && this.previousAction !== '') {
      this.initiateBtnProps();
    }
  }
  /**
   * to show warning message if pagelimit entered is more than 100(more than 1000 is not allowed)
   * @param pageLt :pagelimit entered
   */
  showWarningPagelimit(pageLt: number): void {
    this.touched = true;
    this.plchecked = pageLt;
    const message = this.translate.instant('PAGELIMIT_CONFIRMATION')?.replace('{number of records}', pageLt);
    this.confirmationDialogService.confirm({
      message,
      accept: () => {
        /* empty */
      },
      reject: () => {
        this.txtVal = this.oldvalue;
      }
    });
  }
  initiateBtnProps() {
    if (this.element.name === 'confirmationText') {
      if (this.formData?.action === 'delete') {
        this.txtVal = 'Are you sure that you want to delete the selected records ?';
        this.disableField = true;
      } else if (this.formData?.action === 'cancel') {
        this.txtVal = 'Do you want to discard all unsaved changes?';
        this.disableField = true;
      } else if (this.formData?.action === 'call_a_backend_webservice' && this.formData?.wfAction) {
        this.txtVal = `Do you want to ${this.formData?.wfAction} the record ?`;
        this.disableField = false;
      }
      this.modelValue = this.txtVal;
      this.set();
    }

    if (
      (this.formData?.action === 'delete' ||
        this.formData?.action === 'cancel' ||
        (this.formData?.action === 'call_a_backend_webservice' && this.formData?.wfAction)) &&
      (this.element.name === 'confirmationTitle' ||
        this.element.name === 'confirmationText' ||
        this.element.name === 'confirmationButtonText' ||
        this.element.name === 'cancelButtonText')
    ) {
      this.disableField = false;
    }
    this.previousAction = this.formData?.action;
  }

  ngOnDestroy(): void {
    //AutoUnsubscribe
    $(this.elementRef.nativeElement).remove();
  }
}


